function TermsOfUseLucasAIEs() {
  return (
    <div>
      <h1>Lucas AI - Términos de Uso</h1>

      <p>
        <strong>Última actualización:</strong> 14 de octubre de 2025
      </p>
      <p>
        <strong>Versión:</strong> 5
      </p>

      <p>
        Este Acuerdo de Términos de Uso (este "Acuerdo") es un contrato legalmente vinculante entre la persona física o entidad
        que acepta estos términos ("Usuario", "usted" o "su") y Lucas Health Corporation, una corporación de Delaware que opera
        como Lucas AI ("Lucas AI", "nosotros", "nos", o "nuestro"). Este Acuerdo rige su acceso y uso de la plataforma Lucas AI,
        incluyendo cualquier software, servicios y sitios web relacionados (colectivamente, el "Servicio").
      </p>

      <p>
        Al crear una cuenta, hacer clic en "Acepto", o al acceder o usar el Servicio de cualquier manera, usted afirma que ha
        leído, comprende plenamente y acepta quedar legalmente obligado por este Acuerdo completo. Si está entrando en este
        Acuerdo en nombre de una empresa u otra entidad legal, usted declara y garantiza que tiene plena autoridad legal para
        obligar a dicha entidad a este Acuerdo.
      </p>

      <div className="warning">
        ESTE ACUERDO CONTIENE UNA CLÁUSULA DE ARBITRAJE OBLIGATORIO, UNA RENUNCIA A DEMANDAS COLECTIVAS Y OBLIGACIONES INTEGRALES
        DE INDEMNIZACIÓN. EL SERVICIO ES UNA HERRAMIENTA DE ASISTENCIA Y SE PROPORCIONA SIN GARANTÍA DE EXACTITUD. USTED, EL
        USUARIO, ES EL ÚNICO RESPONSABLE FINAL DE LA EXACTITUD DE TODA DOCUMENTACIÓN MÉDICA Y DE CUALQUIER CONSECUENCIA FINANCIERA
        O REGULATORIA RESULTANTE. POR FAVOR LEA ESTE DOCUMENTO COMPLETO CON DETENCIÓN.
      </div>

      <div className="section">
        <h2>1. El Servicio: Propósito y Limitaciones</h2>

        <h3>1.1. Propósito</h3>
        <p>
          El Servicio es una herramienta ambiental de inteligencia artificial de apoyo para documentación clínica diseñada
          exclusivamente para asistir a proveedores de atención médica calificados ("Proveedores") capturando y ayudando a
          documentar interacciones paciente-proveedor.
        </p>

        <h3>1.2. Uso Previsto para la Integridad de la Documentación Clínica y Marco de Asistencia</h3>
        <p>
          Usted reconoce que el Servicio está diseñado para funcionar como una herramienta avanzada de integridad de documentación
          clínica. Su propósito fundamental es ayudarle a crear notas clínicas que sean una representación completa, precisa y
          coherente del encuentro con el paciente y de su correspondiente juicio médico.
        </p>

        <p>
          Reconocemos las limitaciones inherentes de la documentación ambiental, en la que el proceso de pensamiento clínico del
          proveedor puede no ser completamente verbalizado en todo momento. Para ayudar a cerrar esta brecha, el Servicio puede
          presentar recordatorios o indicaciones contextuales. Usted entiende y acepta que dichos recordatorios no son directrices
          ni recomendaciones de tratamiento. Son herramientas de apoyo diseñadas para recordarle posibles elementos de
          documentación o datos clínicos relevantes que se alinean con los estándares de atención, ayudándole a asegurar que la
          nota final refleje integralmente su evaluación y plan clínico.
        </p>

        <p>
          El Servicio está diseñado para ser neutral respecto a cualquier tratamiento específico, producto comercial, metodología
          propietaria o servicio clínico. Su lógica se fundamenta en lineamientos reconocidos de documentación clínica y buenas
          prácticas, y no tiene como finalidad promover o favorecer una vía clínica o interés comercial particular. Usted conserva
          la responsabilidad exclusiva por todas las decisiones clínicas y acepta utilizar el Servicio conforme a este uso
          previsto.
        </p>

        <h3>1.3. Limitaciones Fundamentales</h3>
        <p>Usted reconoce y acepta que:</p>
        <ul>
          <li>
            (a) El Servicio es una herramienta diseñada para asistir en la documentación, y no para crear registros médicos
            finales, verificados o firmados.
          </li>
          <li>(b) La información generada por el Servicio constituye un borrador y no sustituye su juicio médico profesional.</li>
          <li>(c) Los resultados pueden variar en función de múltiples factores y circunstancias.</li>
          <li>
            (d) Toda responsabilidad final relacionada con decisiones clínicas, exactitud de la documentación, codificación,
            facturación y cumplimiento regulatorio recae exclusivamente en usted.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>2. Responsabilidades del Proveedor, Garantías y Consentimiento del Paciente</h2>

        <h3>2.1. Categorías de Usuario</h3>
        <p>
          Este Acuerdo de Términos de Uso (este "Acuerdo") es un contrato legalmente vinculante entre la persona física o entidad
          legal que acepta estos términos y Lucas Health Corporation, una corporación del estado de Delaware que opera bajo el
          nombre de Lucas AI ("Lucas AI", "nosotros", "nos" o "nuestro"). Si usted es una persona física que se suscribe para su
          propio uso, usted es el "Usuario" ("usted" o "su"). Si actúa en representación de una empresa u otra entidad legal,
          dicha entidad es el "Usuario". Este Acuerdo regula el acceso y uso del Usuario de la plataforma Lucas AI, incluyendo
          cualquier software, servicios y sitios web relacionados (colectivamente, el "Servicio").
        </p>

        <p>
          Al crear una cuenta, hacer clic en "Acepto", o acceder o utilizar el Servicio de cualquier manera, usted afirma que ha
          leído, comprende completamente y acepta quedar legalmente obligado por la totalidad de este Acuerdo. Si está aceptando
          este Acuerdo en nombre de una empresa u otra entidad legal (una "Organización"), usted declara y garantiza que cuenta
          con plena autoridad legal para obligar a dicha entidad y a su personal a este Acuerdo, y este Acuerdo regirá todo acceso
          y uso del Servicio por parte de dicha Organización y su personal autorizado.
        </p>

        <p>El acceso al Servicio se otorga a dos categorías de individuos:</p>
        <ul>
          <li>
            (a) <strong>Profesionales de Salud Licenciados (LHPs):</strong> Individuos que son profesionales de salud licenciados
            (por ejemplo, médicos, PAs, NPs) y que se encuentran en regla ante todas las juntas de licenciamiento aplicables.
          </li>
          <li>
            (b) <strong>Personal Autorizado:</strong> Personal clínico, administrativo y operativo (incluyendo, entre otros,
            enfermeras, asistentes médicos, escribientes, codificadores, facturadores y administradores) que estén empleados o
            contratados por una organización de atención médica suscriptora ("Organización") y hayan recibido acceso por parte de
            dicha Organización.
          </li>
        </ul>

        <h3>2.2. Acceso Basado en Roles y Principio de Mínimo Necesario</h3>
        <p>
          Usted reconoce que su nivel de acceso al Servicio está determinado por el rol específico y los permisos otorgados por su
          Organización, conforme al principio de acceso de "mínimo necesario" requerido para desempeñar sus funciones.
        </p>

        <h3>2.3. Declaración y Garantía sobre el Consentimiento del Paciente</h3>
        <p>
          Como condición material de este Acuerdo, usted declara y garantiza a Lucas AI que ha proporcionado todos los avisos
          necesarios y ha obtenido todos los consentimientos válidos y legalmente requeridos de cada individuo (incluyendo
          pacientes) cuyas conversaciones serán capturadas por el Servicio antes de iniciar cualquier grabación. Usted reconoce
          que esta es una obligación exclusiva y no delegable.
        </p>

        <h3>2.4. Cumplimiento de Leyes sobre Grabación</h3>
        <p>
          Usted es el único responsable de cumplir con todas las leyes federales, estatales y locales aplicables relacionadas con
          la grabación de comunicaciones. Usted acepta que, entre usted y Lucas AI, usted es el controlador de los datos de
          cualquier Información de Salud Protegida (PHI) capturada por el Servicio.
        </p>

        <h3>2.5. Responsabilidad e Indemnización por Consentimiento</h3>
        <p>
          Usted asume toda responsabilidad y acepta indemnizar a Lucas AI por cualquier y todas las Reclamaciones (según se
          definen en la Sección 8 de este Acuerdo) que surjan de su incumplimiento de obtener un consentimiento del paciente
          legalmente suficiente.
        </p>

        <h3>2.6. Responsabilidad Profesional Universal</h3>
        <p>
          Independientemente de su rol, usted acepta usar el Servicio únicamente para fines legítimos y autorizados, mantener la
          confidencialidad de toda información y cumplir con todos los estándares profesionales y éticos aplicables.
        </p>

        <h3>2.7. Derecho a Suspender</h3>
        <p>
          Lucas AI se reserva el derecho de suspender o terminar cualquier cuenta que se determine que ha incumplido las
          obligaciones establecidas en esta sección.
        </p>
      </div>

      <div className="section">
        <h2>3. Procesamiento de Datos, Analítica y Retención</h2>

        <h3>3.1. Propiedad y Procesamiento de Datos</h3>
        <p>
          Entre usted y Lucas AI, usted conserva todos los derechos, títulos e intereses sobre la Información de Salud Protegida
          (PHI, por sus siglas en inglés) que usted envía al Servicio. Usted nos autoriza a procesar todos los datos que envíe al
          Servicio, incluyendo archivos de audio que contengan PHI, con el propósito de proporcionarle el Servicio, según se
          describe en nuestro Acuerdo de Asociado Comercial (BAA, por sus siglas en inglés).
        </p>

        <h3>3.2. Mejora del Servicio y Analítica</h3>
        <p>
          Para ayudarnos a mejorar la calidad, precisión y funcionalidad del Servicio, usted otorga a Lucas AI el derecho a
          utilizar los datos procesados a través del Servicio para control interno de calidad, análisis del tono emocional y
          contenido de las interacciones, y para entrenar y mejorar nuestro software subyacente y modelos de inteligencia
          artificial. Para cualquiera de dichos fines, solo utilizaremos datos que hayan sido desidentificados de acuerdo con los
          estándares establecidos en la Regla de Privacidad HIPAA (45 C.F.R. § 164.514(b)). Lucas AI es el propietario exclusivo
          de todos dichos Datos Desidentificados.
        </p>

        <h3>3.3. Política de Retención de Grabaciones de Audio y Transcripciones</h3>
        <p>
          Usted reconoce y acepta nuestra política estándar de retención de datos: las grabaciones de audio y las transcripciones
          de texto sin procesar generadas a partir de ellas se almacenan temporalmente de manera segura y en cumplimiento con
          HIPAA solo durante el tiempo necesario para generar el resumen de la nota clínica y realizar los controles de calidad
          correspondientes. Una vez completado este proceso, las grabaciones de audio originales y las transcripciones sin
          procesar se eliminan de forma permanente e irreversible de nuestros sistemas. Los resúmenes de texto finales generados
          permanecen como parte de la documentación médica accesible para usted.
        </p>

        <p>
          <strong>Excepción para Estudios de Investigación Contratados:</strong> Nuestra política estándar de eliminación se
          aplicará en todos los casos, salvo que sea modificada mediante un "Acuerdo de Investigación" separado y por escrito
          entre Lucas AI y la entidad legal que lo emplea o con la que usted está afiliado (su "Organización"). Como condición
          previa a cualquier Acuerdo de Investigación y a cualquier retención posterior de datos de audio, la Organización debe
          proporcionar a Lucas AI documentación verificable del consentimiento informado legalmente suficiente del paciente para
          el propósito específico de investigación, lo cual, cuando sea requerido por la ley, deberá incluir la aprobación de una
          Junta de Revisión Institucional (IRB). Lucas AI solo conservará grabaciones de audio y/o transcripciones sin procesar
          más allá del periodo estándar de eliminación tras recibir dicha documentación verificada y únicamente de conformidad con
          los términos del Acuerdo de Investigación aplicable. Si usted es un Usuario que participa en un estudio cubierto por
          dicho acuerdo, usted reconoce esta política modificada de retención y acepta cumplir estrictamente con todos los
          protocolos de investigación y procedimientos de consentimiento establecidos por su Organización.
        </p>
      </div>

      <div className="section">
        <h2>4. Seguridad de la Cuenta y del Usuario</h2>
        <p>
          Usted debe proporcionar información precisa y actualizada al registrarse. Usted es el único y total responsable de toda
          actividad que ocurra bajo su cuenta. Debe implementar y mantener medidas administrativas, físicas y técnicas razonables
          para proteger las credenciales de su cuenta. Usted debe notificar inmediatamente a Lucas AI por escrito sobre cualquier
          uso no autorizado o violación de seguridad relacionada con su cuenta. Lucas AI no será responsable por ninguna pérdida o
          daño que surja de su incumplimiento de estas obligaciones de seguridad.
        </p>
      </div>

      <div className="section">
        <h2>5. Propiedad Intelectual y Titularidad del Servicio</h2>

        <h3>5.1. Propiedad</h3>
        <p>
          Usted reconoce y acepta que el Servicio, incluyendo su software subyacente, código fuente, arquitectura, algoritmos y
          todos los derechos de propiedad intelectual asociados, es propiedad única y exclusiva de Lucas AI. El Servicio se le
          otorga bajo una licencia limitada, revocable, no exclusiva, no sublicenciable y no transferible; no se vende.
        </p>

        <h3>5.2. Restricciones</h3>
        <p>Se le prohíbe expresamente:</p>
        <ul>
          <li>(a) crear obras derivadas del Servicio;</li>
          <li>(b) intentar acceder al código fuente u otros materiales no públicos;</li>
          <li>(c) sublicenciar, vender, alquilar o comercializar el Servicio sin permiso expreso por escrito;</li>
          <li>(d) copiar, descompilar, modificar o realizar ingeniería inversa del Servicio o su software.</li>
        </ul>

        <h3>5.3. Marcas Registradas</h3>
        <p>Las marcas LUCAS AI y los logotipos asociados son marcas comerciales exclusivas de nuestra propiedad.</p>
      </div>

      <div className="section">
        <h2>6. Conducta Prohibida y Uso Previsto</h2>
        <p>
          Su uso del Servicio debe ser legal, ético y exclusivamente para su finalidad prevista. Le está estrictamente prohibido
          utilizar el Servicio para:
        </p>
        <ul>
          <li>(a) Crear documentación médica falsa, engañosa, fraudulenta o generada artificialmente;</li>
          <li>
            (b) Fabricar documentación de cumplimiento para servicios no prestados o encuentros con pacientes que no ocurrieron;
          </li>
          <li>
            (c) Generar documentación para crear artificialmente o "diseñar retrospectivamente" registros de cumplimiento médico;
          </li>
          <li>(d) Facilitar o justificar la sobreutilización o mal uso de tratamientos médicos o productos farmacéuticos;</li>
          <li>
            (e) Participar en cualquier actividad que constituya, facilite o pueda interpretarse como fraude a Medicare, fraude a
            Medicaid, fraude de seguros, abuso de facturación de atención médica, o una violación de cualquier ley local, estatal
            o federal aplicable, incluyendo pero no limitado al Estatuto Anti-Soborno, la Ley de Auto-Referencia del Médico (Ley
            Stark) o la Ley de Reclamaciones Falsas;
          </li>
          <li>
            (f) Usar cualquier proceso automatizado (como un robot o scraper) para acceder al Servicio para cualquier propósito
            que no sea su uso previsto; o introducir virus, caballos de Troya, gusanos u otro material malicioso o
            tecnológicamente dañino; o intentar obtener acceso no autorizado, interferir, dañar o interrumpir cualquier parte del
            Servicio, el servidor en el que se almacena el Servicio, o cualquier servidor, computadora o base de datos conectada
            al Servicio.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>7. Suscripción, Tarifas y Cancelación</h2>

        <h3>7.1. Suscripción, Pagos Automáticos y Facturación</h3>
        <p>
          Para acceder al Servicio, usted debe proporcionar y mantener un método de pago válido y autorizado registrado, como una
          tarjeta de crédito (su "Método de Pago"). Una suscripción activa requiere un Método de Pago activo con autorización para
          pagos automáticos. El Servicio se ofrece en modalidad de suscripción y se factura por adelantado de forma recurrente y
          periódica (un "Ciclo de Facturación"). Su Ciclo de Facturación será especificado en el momento de la compra. Al
          proporcionar su Método de Pago y aceptar estos Términos, usted autoriza expresamente a Lucas AI a cargar automáticamente
          las tarifas de suscripción aplicables a su Método de Pago para cada Ciclo de Facturación recurrente hasta que usted
          cancele su suscripción de conformidad con la Sección 7.2. Usted es responsable de garantizar que la información de su
          Método de Pago se mantenga actualizada, válida y precisa en todo momento.
        </p>

        <p>
          Si su Método de Pago es rechazado o si un cargo no se procesa con éxito, le notificaremos y podremos intentar procesar
          el pago nuevamente. Nos reservamos el derecho de suspender su acceso al Servicio sin responsabilidad hasta que el pago
          pendiente sea recibido en su totalidad. Esta suspensión puede impedirle acceder a cualquier dato, incluyendo
          documentación de pacientes, almacenado en el Servicio. Nos reservamos el derecho de cancelar su cuenta por falta de pago
          prolongada o repetida. Para efectos de esta sección, "falta de pago prolongada o repetida" se define como cualquier
          instancia en la cual las tarifas permanezcan impagas durante más de sesenta (60) días desde la fecha de vencimiento o
          después de tres (3) intentos fallidos de cobro consecutivos.
        </p>

        <h3>7.2. Responsabilidad del Usuario para la Cancelación</h3>
        <p>
          Usted es el único responsable de gestionar y cancelar su suscripción. Para evitar cargos por el siguiente Ciclo de
          Facturación, debe cancelar su suscripción antes del inicio de dicho ciclo. Puede cancelar su suscripción en cualquier
          momento mediante uno de los siguientes métodos:
        </p>
        <ul>
          <li>
            (a) Accediendo a la sección de perfil o configuración de cuenta dentro de la aplicación del Servicio y siguiendo los
            procedimientos de cancelación;
          </li>
          <li>(b) Enviando una solicitud de cancelación por escrito a: accounting@lucashealth.ai.</li>
        </ul>
        <p>
          Usted reconoce que las tarifas de suscripción le otorgan acceso al Servicio y no están basadas en su uso real. Por lo
          tanto, no usar el Servicio o la falta de actividad durante un Ciclo de Facturación pagado no lo libera de sus
          obligaciones de pago y no constituye un motivo válido para un reembolso. Tras la cancelación exitosa, su suscripción no
          se renovará y su acceso al Servicio continuará hasta el final de su Ciclo de Facturación ya pagado.
        </p>

        <h3>7.3. Política de Reembolsos</h3>
        <p>
          Excepto cuando lo exija la ley, todas las tarifas no son reembolsables. Cualquier reembolso, crédito o ajuste que pueda
          ofrecerse se otorga estrictamente caso por caso y a la sola y absoluta discreción de Lucas Health Corporation. Una
          decisión de otorgar un reembolso en un caso no crea obligación ni establece precedente para otorgar reembolsos en otros
          casos.
        </p>

        <h3>7.4. Cambios en los Precios</h3>
        <p>
          Excepto cuando se especifique lo contrario en un acuerdo separado y firmado entre usted y Lucas AI que establezca un
          plazo y precio fijo, nos reservamos el derecho de modificar las tarifas de suscripción del Servicio. Le proporcionaremos
          al menos treinta (30) días de aviso previo para cualquier cambio de tarifa. Los cambios de precio entrarán en vigor al
          inicio del siguiente Ciclo de Facturación después del período de aviso. Su uso continuo del Servicio después del cambio
          de precio constituye su aceptación de pagar el monto modificado.
        </p>

        <h3>7.5. Impuestos</h3>
        <p>
          Todas las tarifas son exclusivas de cualquier impuesto federal, estatal o local, arancel o cargo aplicable
          (colectivamente, "Impuestos"). Usted es el único responsable de pagar todos los Impuestos asociados con su suscripción
          al Servicio.
        </p>

        <h3>7.6. Pruebas Gratuitas y Promociones</h3>
        <p>
          Lucas AI puede, a su exclusiva discreción, ofrecer una suscripción con un período de prueba gratuito por un tiempo
          limitado. Si usted se registra para una prueba gratuita, deberá proporcionar su Método de Pago. Comenzaremos a facturar
          a su Método de Pago las tarifas de suscripción regulares el primer día posterior al final del período de prueba
          gratuito, de forma recurrente, a menos que cancele su suscripción antes del final del período de prueba de conformidad
          con la Sección 7.2.
        </p>
      </div>

      <div className="section">
        <h2>8. Indemnización (Su Obligación Integral de Protegernos)</h2>
        <p>
          Usted acepta defender, indemnizar y mantener indemne a Lucas AI, sus distribuidores autorizados, afiliados, directores,
          funcionarios, empleados y agentes (colectivamente, las "Partes Indemnizadas") de cualquier y toda reclamación, demanda,
          acción, proceso, investigación gubernamental, auditoría, sentencia, pérdida, daño, responsabilidad, multa, penalidad y
          gasto de cualquier tipo (incluyendo honorarios razonables de abogados y costos legales), así como cualquier demanda de
          recuperación o devolución de pagos de cualquier pagador (colectivamente, "Reclamaciones") que surjan o estén
          relacionadas con:
        </p>
        <ul>
          <li>(a) Su incumplimiento material de este Acuerdo o violación de la ley aplicable;</li>
          <li>(b) Su mala praxis clínica, negligencia profesional o violación de los estándares de práctica profesional;</li>
          <li>
            (c) Sus prácticas de documentación, facturación o codificación, incluyendo Reclamaciones derivadas de documentación
            fraudulenta o facturación intencionalmente fraudulenta;
          </li>
          <li>
            (d) Cualquier Reclamación resultante de la inexactitud, incompletitud o insuficiencia de cualquier documentación
            generada por el Servicio que usted no haya revisado, corregido y verificado de manera independiente y exhaustiva.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>9. Exclusiones de Garantía y Limitación de Responsabilidad</h2>

        <h3>9.1. Exención de Garantía</h3>
        <div className="warning">
          EL SERVICIO SE PROPORCIONA "TAL CUAL" Y "SEGÚN DISPONIBILIDAD", CON TODOS LOS DEFECTOS Y SIN GARANTÍA DE NINGÚN TIPO. EN
          LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, LUCAS AI RENUNCIA A TODAS LAS GARANTÍAS, EXPRESAS, IMPLÍCITAS O ESTATUTARIAS,
          INCLUYENDO GARANTÍAS IMPLÍCITAS DE COMERCIABILIDAD, IDONEIDAD PARA UN USO PARTICULAR, TÍTULO Y NO INFRACCIÓN. NO
          GARANTIZAMOS QUE EL SERVICIO ESTÉ LIBRE DE ERRORES NI QUE LA INFORMACIÓN OBTENIDA SEA PRECISA O CONFIABLE. NO HAY
          GARANTÍA SOBRE LA EXACTITUD, COMPLETITUD O ADECUACIÓN DEL CONTENIDO PARA CUALQUIER PROPÓSITO, INCLUYENDO DOCUMENTACIÓN
          CLÍNICA O FACTURACIÓN.
        </div>

        <h3>9.2. Exclusión de Daños</h3>
        <div className="warning">
          BAJO NINGUNA CIRCUNSTANCIA LUCAS AI O SUS AFILIADOS SERÁN RESPONSABLES ANTE USTED POR DAÑOS INCIDENTALES, CONSECUENTES,
          INDIRECTOS, ESPECIALES, PUNITIVOS O EJEMPLARES.
        </div>

        <h3>9.3. Límite Máximo de Responsabilidad</h3>
        <div className="warning">
          EN NINGÚN CASO LA RESPONSABILIDAD TOTAL DE LUCAS AI EXCEDERÁ EL MAYOR DE: (A) LAS TARIFAS TOTALES PAGADAS POR USTED
          DURANTE LOS DOCE (12) MESES PREVIOS A LA RECLAMACIÓN; O (B) CIEN DÓLARES ESTADOUNIDENSES (US$100.00).
        </div>
      </div>

      <div className="section">
        <h2>10. Responsabilidad Exclusiva del Usuario sobre Verificación, Exactitud y Resultados</h2>

        <h3>10.1. Reconocimiento de Limitaciones de IA</h3>
        <p>
          Usted entiende que el Servicio utiliza inteligencia artificial en evolución. El contenido generado puede contener
          errores, omisiones o interpretaciones incorrectas. No es documentación final y no debe tratarse como tal.
        </p>

        <h3>10.2. Deber Absoluto, Primario y No Delegable de Verificar</h3>
        <p>
          Usted tiene el deber absoluto, primario y no delegable de revisar, editar y verificar cada parte del contenido generado
          antes de firmarlo, aprobarlo o utilizarlo para cualquier fin. Debe corregir todas las inexactitudes para garantizar que
          la documentación final refleje fielmente el encuentro clínico.
        </p>

        <h3>10.3. Responsabilidad Exclusiva por Consecuencias</h3>
        <p>
          Usted es el único responsable de todo el contenido final y sus resultados. Lucas AI no tendrá responsabilidad alguna por
          consecuencias adversas derivadas del uso del contenido generado, incluyendo:
        </p>
        <ul>
          <li>(a) decisiones clínicas inapropiadas;</li>
          <li>(b) tergiversación o actividad fraudulenta;</li>
          <li>(c) reclamaciones denegadas, auditorías o recuperaciones;</li>
          <li>(d) investigaciones regulatorias o sanciones;</li>
          <li>(e) cualquier acción que pueda provocar intervención regulatoria.</li>
        </ul>
      </div>

      <div className="section">
        <h2>11. Terminación</h2>

        <h3>11.1. Terminación por Nuestra Parte</h3>
        <p>
          Podemos suspender o terminar su cuenta y este Acuerdo inmediatamente y sin aviso por cualquier incumplimiento material,
          incluyendo falta de pago o violación de la Sección 6.
        </p>

        <h3>11.2. Efecto de Terminación</h3>
        <p>
          Al terminar, su acceso al Servicio cesará de inmediato. Usted sigue siendo responsable de tarifas acumuladas hasta la
          fecha de terminación. Posteriormente, Lucas AI podrá eliminar permanentemente sus datos según el BAA y la ley aplicable.
        </p>

        <h3>11.3. Supervivencia</h3>
        <p>Las siguientes secciones permanecerán en vigor tras cualquier terminación: 2, 3, 4, 5, 6, 7, 8, 9, 10, 12 y 13.</p>
      </div>

      <div className="section">
        <h2>12. Ley Aplicable y Resolución de Disputas</h2>

        <h3>12.1. Ley Aplicable</h3>
        <p>Este Acuerdo se rige por las leyes del Estado de Florida.</p>

        <h3>12.2. Arbitraje Obligatorio</h3>
        <p>
          Todas las disputas se resolverán exclusivamente mediante arbitraje final y vinculante en el condado de Miami-Dade,
          Florida.
        </p>

        <h3>12.3. Renuncia a Acciones Colectivas</h3>
        <div className="warning">
          Usted y Lucas AI acuerdan presentar reclamaciones únicamente a título individual y no como demandante ni como miembro de
          una acción colectiva o procedimiento representativo de ningún tipo.
        </div>
      </div>

      <div className="section">
        <h2>13. Disposiciones Misceláneas</h2>

        <h3>13.1. Acuerdo Completo</h3>
        <p>
          Este Acuerdo, junto con nuestra <strong>Política de Privacidad</strong> y el{" "}
          <strong>Acuerdo de Asociado Comercial (BAA, por sus siglas en inglés)</strong>, constituye el{" "}
          <strong>acuerdo completo</strong> entre usted y Lucas AI con respecto al Servicio. El BAA queda incorporado por
          referencia a este Acuerdo. En caso de conflicto entre los términos de este Acuerdo y los términos del BAA en relación
          con el manejo de la Información de Salud Protegida (PHI, por sus siglas en inglés),{" "}
          <strong>prevalecerán los términos del BAA</strong>.
        </p>

        <h3>13.2. Divisibilidad</h3>
        <p>
          Si alguna disposición de este Acuerdo se considera <strong>inaplicable o inválida</strong>, dicha disposición será{" "}
          <strong>limitada o eliminada únicamente en la medida mínima necesaria</strong> para que el resto del Acuerdo continúe
          siendo <strong>válido, vinculante y plenamente vigente</strong>.
        </p>

        <h3>13.3. Información de Contacto</h3>
        <p>
          Para avisos legales, puede comunicarse con nosotros en: <strong>legal@lucashealth.ai</strong>
        </p>

        <h3>13.4. Notificaciones</h3>
        <p>
          Cualquier aviso u otra comunicación emitida por Lucas AI en virtud de este Acuerdo, incluyendo aquellas relativas a
          modificaciones del mismo, se realizará: (i) mediante correo electrónico a la dirección asociada con su cuenta; o (ii)
          mediante publicación dentro del propio Servicio. Para las notificaciones enviadas por correo electrónico, se considerará
          como <strong>fecha de recepción</strong> la fecha en la que dicho correo haya sido transmitido.
        </p>

        <h3>13.5. Fuerza Mayor</h3>
        <p>
          Ninguna de las partes será responsable por cualquier incumplimiento o demora en el cumplimiento de este Acuerdo (excepto
          por el retraso en el pago de sumas adeudadas) debido a causas fuera del control razonable de dicha parte y que ocurran
          sin culpa o negligencia de la misma, incluyendo, pero no limitado a, actos de Dios, actos gubernamentales, inundaciones,
          incendios, disturbios civiles, actos de terrorismo, huelgas u otros conflictos laborales, o fallas en redes informáticas
          o de telecomunicaciones.
        </p>

        <h3>13.6. Cesión</h3>
        <p>
          Usted no podrá ceder ni transferir este Acuerdo, ya sea por efecto de ley o de otro modo, sin el consentimiento previo
          por escrito de Lucas AI. Cualquier intento de cesión o transferencia sin dicho consentimiento será{" "}
          <strong>nulo y sin efecto</strong>. Lucas AI podrá <strong>ceder o transferir libremente</strong> este Acuerdo sin
          restricción. Sujeto a lo anterior, este Acuerdo será vinculante y redundará en beneficio de las partes, sus sucesores y
          cesionarios permitidos.
        </p>

        <h3>13.7. Comentarios y Sugerencias (Feedback)</h3>
        <p>
          Si usted nos proporciona ideas, sugerencias o propuestas relacionadas con el Servicio ("<strong>Comentarios</strong>
          "), usted otorga a Lucas AI un{" "}
          <strong>derecho perpetuo, irrevocable, mundial, libre de regalías y totalmente pagado</strong> para{" "}
          <strong>usar, divulgar, reproducir, modificar y explotar dichos Comentarios</strong> para cualquier propósito, sin
          obligación ni compensación alguna hacia usted.
        </p>
      </div>
    </div>
  );
}

export default TermsOfUseLucasAIEs;
